import { customElement, property, state } from 'lit/decorators.js';
import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import Handlebars from 'handlebars';

const FIELDS = 'fields=webContentTitle,keywords,siteId,renderedContents,dateCreated,dateModified,datePublished';
const NESTED_FIELDS = 'nestedFields=renderedContentValue';

function getStructuredContentByIdUrl(groupId: string, id: string) {
  return `/o/headless-delivery/v1.0/sites/${ groupId }/structured-contents/by-key/${ id }?${ FIELDS }&${ NESTED_FIELDS }`;
}

function getStructuredContentByTitleUrl(groupId: string, webContentTitle: string) {
  return `/o/headless-delivery/v1.0/sites/${ groupId }/structured-contents?flatten=true&search=${ webContentTitle }&filter=webContentTitle eq '${ webContentTitle }'&${ FIELDS }&${ NESTED_FIELDS }`;
}

async function fetchStructuredContent(url: string) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error('Web Content Loading response not okay');
  }
  return res.json();
}

type RenderedContent = {
  contentTemplateId: string;
  contentTemplateName: string;
  renderedContentURL: string;
  renderedContentValue: string;
}

type StructuredContent = {
  webContentTitle: string;
  siteId: string;
  dateCreated: string;
  dateModified: string;
  datePublished: string;
  keywords: string[];
  renderedContents: RenderedContent[];
}

type State = {
  loading: boolean;
  success: boolean;
  error: boolean;
  template?: HandlebarsTemplateDelegate;
}

const DEFAULT_STATE: State = {
  loading: false,
  success: false,
  error: false,
};

@customElement('cv-web-content')
export class WebContent extends LitElement {
  @property({ type: String, attribute: 'web-content-id' })
  webContentId?: string = '';

  @property({ type: String, attribute: 'web-content-title' })
  webContentTitle?: string = '';

  @property({ type: String, attribute: 'scope-group-id' })
  scopeGroupId: string = '';

  @property({ type: Object })
  data: unknown = null;

  @state()
  state: State = DEFAULT_STATE;

  constructor() {
    super();
    this.getStructuredContentByTitle = this.getStructuredContentByTitle.bind(this);
  }

  /* LitElement uses a shadow root by default. We want to render the template */
  createRenderRoot() {
    return this;
  }

  willUpdate(changedProperties: PropertyValues<this>) {
    super.updated(changedProperties);

    if (changedProperties.has('webContentId')) {
      if (this.webContentId) {
        this.getStructuredContentById(this.webContentId);
      } else {
        this.state = { ...this.state, template: undefined };
      }
    } else if (changedProperties.has('webContentTitle') && this.webContentTitle) {
      this.webContentTitle = this.webContentTitle.toString().replace('\r\n', '');
      this.getStructuredContentByTitle(this.webContentTitle, this.scopeGroupId);
    }
  }

  async getStructuredContentById(id: string) {
    try {
      this.state = { ...DEFAULT_STATE, loading: true };
      const url = getStructuredContentByIdUrl(this.scopeGroupId, id);
      const item: StructuredContent = await fetchStructuredContent(url);

      if (item) {
        const webcontent = item.renderedContents?.[ 0 ]?.renderedContentValue;
        this.state = { ...this.state, success: true, template: Handlebars.compile(webcontent) };
      } else {
        this.state = { ...DEFAULT_STATE, success: true };
      }
    } catch (e) {
      console.error(e);
      this.state = { ...DEFAULT_STATE, error: true };
    } finally {
      this.state = { ...this.state, loading: false };
    }
  }

  async getStructuredContentByTitle(webContentTitle: string, groupId: string) {
    try {
      this.state = { ...DEFAULT_STATE, loading: true };
      const url = getStructuredContentByTitleUrl(groupId, webContentTitle);
      const json = await fetchStructuredContent(url);
      const item: StructuredContent = json?.items.find((item: StructuredContent) => item.webContentTitle.toString().includes(webContentTitle.toString()));

      if (item) {
        const webcontent = item.renderedContents?.[ 0 ]?.renderedContentValue;
        this.state = { ...this.state, success: true, template: Handlebars.compile(webcontent) };
      } else {
        this.state = { ...DEFAULT_STATE, success: true };
      }
    } catch (e) {
      console.error(e);
      this.state = { ...DEFAULT_STATE, error: true };
    } finally {
      this.state = { ...this.state, loading: false };
    }
  }

  render(): TemplateResult {
    console.info('SCOPE GROUP ID', this.scopeGroupId);
    console.info('EXTERNAL DATA', this.data);

    const rendered: string = this.state?.template ? this.state.template(this.data) : '';

    if (this.state.loading) {
      //return html`<span>Laden...</span>`;
    }
    if (this.state.error) {
      return html`<span>Inhalt konnte nicht geladen werden.</span>`;
    }

    if (this.state.success && rendered) {
      return html`${ unsafeHTML(rendered) }`;
    }

    if (this.state.success && !rendered) {
      console.warn(`Webcontent für "${ this.webContentTitle }" nicht gefunden.`);
    }

    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cv-web-content': WebContent;
  }
}
